// src/components/AuthModal/index.js
// Unified Authentication Modal — supports both phone_otp and email_password methods
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail, Lock, User, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { settingsService } from '../../config/settingsService';
import { setAuthSession as setAuthCookies } from '../../utils/authStorage';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const getApiHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': X_API_KEY,
  'X-Tenant': getTenantId(),
  'Accept': 'application/json'
});

const OtpBoxInput = ({ length = 4, value = '', onChange }) => {
  const inputRefs = useRef([]);

  const handleChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    const newOtp = (value || '').padEnd(length, ' ').split('');
    newOtp[index] = val.substring(val.length - 1);
    const combined = newOtp.join('').trim();
    onChange(combined);

    if (val && index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = (value || '').padEnd(length, ' ').split('');
      if (newOtp[index] && newOtp[index] !== ' ') {
        newOtp[index] = ' ';
        onChange(newOtp.join('').trim());
      } else if (index > 0) {
        newOtp[index - 1] = ' ';
        onChange(newOtp.join('').trim());
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pastedData) {
      onChange(pastedData);
      const focusIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '15px' }}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="tel"
          maxLength={1}
          placeholder="-"
          value={(value || '')[index] && (value || '')[index] !== ' ' ? (value || '')[index] : ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className="auth-input"
          style={{ width: '55px', height: '55px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold', padding: 0, borderRadius: '8px' }}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
};

const AuthModal = ({ isOpen, onClose, onAuthSuccess, branchId, authMethodProp }) => {
  // Fallback to 'email_password' if not provided
  const authMethod = authMethodProp || 'email_password';
  const navigate = useNavigate();

  // ─── View State ──────────────────────────────────────────
  // phone_otp views: 'phone', 'otp'
  // email_password views: 'login', 'signup-phone', 'signup-otp', 'signup-register', 'forgot', 'forgot-code', 'forgot-reset'
  const [view, setView] = useState(authMethod === 'email_password' ? 'login' : 'phone');

  // ─── Common State ────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // ─── Phone OTP Fields ────────────────────────────────────
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');

  // ─── Email or Phone + Password Login Fields ──────────────
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // ─── Sign-Up Fields ──────────────────────────────────────
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupOtp, setSignupOtp] = useState('');

  // ─── Forgot Password Fields ──────────────────────────────
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotTimer, setForgotTimer] = useState(0);

  // Persistent Timer for Forgot Password Code
  useEffect(() => {
    const checkTimer = () => {
      const expiryStr = localStorage.getItem('fpCodeExpiry');
      if (expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
        setForgotTimer(remaining);
        if (remaining <= 0) {
          localStorage.removeItem('fpCodeExpiry');
        }
      } else {
        setForgotTimer(0);
      }
    };

    checkTimer();
    const interval = setInterval(checkTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatForgotTimer = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ─── Reset on open/close ─────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setView(authMethod === 'email_password' ? 'login' : 'phone');
      setPhoneNumber('');
      setOtp('');
      setLoginIdentifier('');
      setLoginPassword('');
      setSignupFirstName('');
      setSignupLastName('');
      setSignupEmail('');
      setSignupPhone('');
      setSignupOtp('');
      setForgotEmail('');
      setForgotCode('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setAcceptTerms(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen, authMethod]);

  // Clear password & forgot fields when navigating back to login view
  useEffect(() => {
    if (view === 'login') {
      setLoginPassword('');
      setForgotEmail('');
      setForgotCode('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [view]);

  const handleOpenPolicy = (path) => {
    onClose();
    navigate(path);
  };

  // ════════════════════════════════════════════════════════
  //  PHONE OTP FLOW
  // ════════════════════════════════════════════════════════
  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!acceptTerms) {
      setError('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }
    setLoading(true);
    setError('');
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    try {
      await axios.post(`${API_BASE_URL}/auth/customer-login/send-otp`, {
        code: '+91', phone: cleanPhone
      }, { headers: getApiHeaders() });
      setPhoneNumber(cleanPhone);
      setView('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const cleanOtp = otp.replace(/\D/g, '');
    if (!cleanOtp || cleanOtp.length < 4) {
      setError('Please enter a valid OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/customer-login/verify-otp`, {
        code: '+91', phone: cleanPhone, token: cleanOtp, branch_id: branchId || 1
      }, { headers: getApiHeaders() });
      const { token, user } = response.data;
      if (token && user?.id) {
        setAuthCookies(token, user.id, cleanPhone);
        onAuthSuccess(token, user.id, cleanPhone);
        onClose();
      } else {
        setError('Authentication succeeded but incomplete data received.');
      }
    } catch (err) {
      if (err.response?.status === 422) setError('Invalid OTP. Please check and try again.');
      else if (err.response?.status === 401 || err.response?.status === 400) setError('Invalid or expired OTP. Please request a new one.');
      else if (err.response?.status === 429) setError('Too many attempts. Please wait a few minutes.');
      else setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════
  //  EMAIL + PASSWORD LOGIN
  // ════════════════════════════════════════════════════════
  const handleEmailLogin = async () => {
    const identifier = loginIdentifier.trim();
    if (!identifier) { setError('Please enter your email or phone number'); return; }

    const isPhone = /^[0-9+\-\s()]+$/.test(identifier);
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|co\.in|net|org|edu|gov|io|co|biz|info)$/i;
    if (isPhone) {
      const cleanPhone = identifier.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit mobile number');
        return;
      }
    } else {
      if (!emailRegex.test(identifier)) {
        setError('Please enter a valid email address or phone number');
        return;
      }
      if (identifier.toLowerCase().includes('@gmail.') && !identifier.toLowerCase().endsWith('@gmail.com')) {
        setError('Invalid email address. Please enter a valid Gmail address.');
        return;
      }
    }

    if (!loginPassword) { setError('Please enter your password'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        identifier: loginIdentifier.trim(),
        password: loginPassword
      }, { headers: getApiHeaders() });
      const { token, user } = response.data;
      if (token && user?.id) {
        setAuthCookies(token, user.id, user.phone || '');
        onAuthSuccess(token, user.id, user.phone || '');
        onClose();
      } else {
        setError('Login succeeded but incomplete data received.');
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const msg = typeof errors === 'object' ? Object.values(errors).flat().join('. ') : errors;
        setError(msg || 'Invalid credentials');
      } else {
        setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════
  //  SIGN-UP FLOW (email_password mode)
  // ════════════════════════════════════════════════════════
  const handleSignupSendOtp = async () => {
    if (!signupPhone || signupPhone.length < 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!acceptTerms) {
      setError('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }
    setLoading(true);
    setError('');
    const cleanPhone = signupPhone.replace(/\D/g, '');
    try {
      await axios.post(`${API_BASE_URL}/auth/signup/otp`, {
        phone: cleanPhone, code: '+91'
      }, { headers: getApiHeaders() });
      setSignupPhone(cleanPhone);
      setView('signup-otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupVerifyOtp = async () => {
    const cleanOtp = signupOtp.replace(/\D/g, '');
    if (!cleanOtp || cleanOtp.length < 4) {
      setError('Please enter a valid OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/signup/verify`, {
        phone: signupPhone, code: '+91', token: cleanOtp
      }, { headers: getApiHeaders() });
      setView('signup-register');
      setSuccess('Phone verified! Complete your registration below.');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupRegister = async () => {
    if (!signupFirstName.trim()) { setError('First name is required'); return; }
    if (!signupLastName.trim()) { setError('Last name is required'); return; }

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|co\.in|net|org|edu|gov|io|co|biz|info)$/i;
    const lowerSignupEmail = signupEmail.trim().toLowerCase();
    if (!signupEmail.trim()) {
      setError('Email is required');
      return;
    } else if (!emailRegex.test(signupEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    } else if (lowerSignupEmail.includes('@gmail.') && !lowerSignupEmail.endsWith('@gmail.com')) {
      setError('Invalid email address. Please enter a valid Gmail address.');
      return;
    }
    if (!signupPhone || signupPhone.length < 10) { setError('Valid 10-digit mobile number is required'); return; }
    if (!acceptTerms) { setError('Please accept the Terms & Conditions and Privacy Policy'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/signup/register`, {
        first_name: signupFirstName.trim(),
        last_name: signupLastName.trim(),
        email: signupEmail.trim() || undefined,
        phone: signupPhone,
        country_code: '+91'
      }, { headers: getApiHeaders() });
      setSuccess('Registration successful! Check your email for login credentials.');
      setTimeout(() => {
        setSignupFirstName('');
        setSignupLastName('');
        setSignupEmail('');
        setSignupPhone('');
        setSignupOtp('');
        setAcceptTerms(false);
        setView('login');
        setSuccess('');
      }, 5000); // 5 seconds to let them read the email part
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const msg = typeof errors === 'object' ? Object.values(errors).flat().join('. ') : errors;
        setError(msg);
      } else {
        setError(err.response?.data?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════
  //  FORGOT PASSWORD FLOW
  // ════════════════════════════════════════════════════════
  const handleForgotSendCode = async () => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|co\.in|net|org|edu|gov|io|co|biz|info)$/i;
    const lowerForgotEmail = forgotEmail.trim().toLowerCase();
    if (!forgotEmail.trim()) {
      setError('Please enter your email address');
      return;
    } else if (!emailRegex.test(forgotEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    } else if (lowerForgotEmail.includes('@gmail.') && !lowerForgotEmail.endsWith('@gmail.com')) {
      setError('Invalid email address. Please enter a valid Gmail address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
        email: forgotEmail.trim()
      }, { headers: getApiHeaders() });
      const expiryMins = parseInt(process.env.REACT_APP_RESET_CODE_EXPIRY_MINUTES, 10) || 15;
      localStorage.setItem('fpCodeExpiry', (Date.now() + expiryMins * 60 * 1000).toString());
      setForgotTimer(expiryMins * 60);
      setView('forgot-code');
      setSuccess('Reset code sent! Check your email.');
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors?.email) setError(errors.email[0]);
      else setError(err.response?.data?.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerifyCode = async () => {
    if (forgotTimer <= 0) {
      setError('Reset code has expired. Please request a new code.');
      setForgotCode('');
      return;
    }
    if (!forgotCode.trim() || forgotCode.trim().length !== 4) { setError('Please enter a valid 4-digit reset code'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password/verify-code`, {
        email: forgotEmail.trim(), code: forgotCode.trim()
      }, { headers: getApiHeaders() });
      localStorage.removeItem('fpCodeExpiry');
      setForgotTimer(0);
      setView('forgot-reset');
      setSuccess('Code verified! Set your new password.');
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors?.code) setError(errors.code[0]);
      else setError(err.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot-password/reset-password`, {
        email: forgotEmail.trim(),
        password: newPassword,
        password_confirmation: confirmPassword
      }, { headers: getApiHeaders() });
      setSuccess('Password reset successfully! You can now log in.');
      // If a token is returned, auto-login
      if (response.data?.token) {
        setTimeout(() => {
          setView('login');
          setSuccess('');
        }, 2000);
      } else {
        setTimeout(() => {
          setView('login');
          setSuccess('');
        }, 2500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Terms & Conditions UI ──────────────────────────────
  const TermsCheckbox = () => (
    <div className="auth-terms">
      <input
        type="checkbox"
        id="auth-terms"
        checked={acceptTerms}
        onChange={(e) => { setAcceptTerms(e.target.checked); setError(''); }}
        className="auth-checkbox"
      />
      <label htmlFor="auth-terms" className="auth-terms-label">
        I accept the{' '}
        <a href="#" onClick={(e) => { e.preventDefault(); handleOpenPolicy('/terms-conditions'); }} className="auth-link">
          Terms & Conditions
        </a>
        {' '}and{' '}
        <a href="#" onClick={(e) => { e.preventDefault(); handleOpenPolicy('/privacy-policy'); }} className="auth-link">
          Privacy Policy
        </a>
      </label>
    </div>
  );

  if (!isOpen) return null;

  // ════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════
  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="auth-close-btn" aria-label="Close">
          <X size={22} />
        </button>

        <div className="auth-content">
          {success && (
            <div className="auth-toast auth-toast-success">
              <CheckCircle size={20} className="auth-success-icon" />
              <div className="auth-toast-content">
                <strong>Great job!</strong>
                <p>{success}</p>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════ */}
          {/* PHONE OTP - STEP 1: Enter Phone */}
          {/* ════════════════════════════════════════════ */}
          {view === 'phone' && (
            <>
              <div className="auth-header">
                <div className="auth-icon-circle"><Phone size={24} /></div>
                <h2 className="auth-title">Welcome</h2>
                <p className="auth-subtitle">Enter your mobile number to continue</p>
              </div>

              <div className="auth-input-group">
                <div className="auth-input-wrapper">
                  <span className="auth-input-prefix">+91</span>
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (v.length <= 10) { setPhoneNumber(v); setError(''); }
                    }}
                    maxLength={10}
                    className="auth-input auth-input-with-prefix"
                  />
                </div>
              </div>

              <TermsCheckbox />
              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleSendOTP}
                disabled={loading || !phoneNumber || phoneNumber.length < 10 || !acceptTerms}
                className="auth-btn auth-btn-primary"
              >
                {loading ? <><Loader size={18} className="auth-spinner" /> Sending...</> : 'Send OTP'}
              </button>
            </>
          )}

          {/* ════════════════════════════════════════════ */}
          {/* PHONE OTP - STEP 2: Enter OTP */}
          {/* ════════════════════════════════════════════ */}
          {view === 'otp' && (
            <>
              <div className="auth-header">
                <div className="auth-icon-circle"><Lock size={24} /></div>
                <h2 className="auth-title">Verify OTP</h2>
                <p className="auth-subtitle">
                  We've sent an OTP to <strong>+91 {phoneNumber}</strong>
                </p>
              </div>

              <div className="auth-input-group">
                <input
                  type="tel"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    if (v.length <= 6) { setOtp(v); setError(''); }
                  }}
                  maxLength={6}
                  className="auth-input auth-input-otp"
                />
              </div>

              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleVerifyOTP}
                disabled={loading || !otp || otp.length < 4}
                className="auth-btn auth-btn-primary"
              >
                {loading ? <><Loader size={18} className="auth-spinner" /> Verifying...</> : 'Verify OTP'}
              </button>

              <div className="auth-footer-links">
                <button onClick={() => { setOtp(''); setError(''); handleSendOTP(); }} className="auth-text-btn" disabled={loading}>
                  Resend OTP
                </button>
                <span className="auth-divider-dot">•</span>
                <button onClick={() => { setView('phone'); setError(''); }} className="auth-text-btn">
                  Change Number
                </button>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════ */}
          {/* EMAIL + PASSWORD LOGIN */}
          {/* ════════════════════════════════════════════ */}
          {view === 'login' && (
            <>
              <div className="auth-header">
                <div className="auth-icon-circle"><Mail size={24} /></div>
                <h2 className="auth-title">Welcome Back</h2>
                <p className="auth-subtitle">Log in with your email or phone number</p>
              </div>

              <div className="auth-input-group">
                <div className="auth-input-wrapper">
                  {/^\d+$/.test(loginIdentifier) ? <Phone size={18} className="auth-input-icon" /> : <Mail size={18} className="auth-input-icon" />}
                  <input
                    type="text"
                    placeholder="Email or phone number"
                    value={loginIdentifier}
                    onChange={(e) => {
                      let v = e.target.value;
                      if (/^\d+$/.test(v) && v.length > 10) { v = v.substring(0, 10); }
                      setLoginIdentifier(v);
                      setError('');
                    }}
                    className="auth-input auth-input-with-icon"
                  />
                </div>
                <div className="auth-input-wrapper">
                  <Lock size={18} className="auth-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={loginPassword}
                    onChange={(e) => { setLoginPassword(e.target.value); setError(''); }}
                    className="auth-input auth-input-with-icon"
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleEmailLogin}
                disabled={loading || !loginIdentifier || !loginPassword}
                className="auth-btn auth-btn-primary"
              >
                {loading ? <><Loader size={18} className="auth-spinner" /> Logging in...</> : 'Log In'}
              </button>

              <div className="auth-footer-links">
                <button onClick={() => { setView('forgot'); setError(''); setSuccess(''); }} className="auth-text-btn">
                  Forgot Password?
                </button>
              </div>

              <div className="auth-switch-mode">
                <span>Don't have an account?</span>
                <button onClick={() => { setView('signup-register'); setError(''); setSuccess(''); }} className="auth-switch-btn">
                  Sign Up
                </button>
              </div>
            </>
          )}



          {/* ════════════════════════════════════════════ */}
          {/* SIGN-UP: Create Account (Direct) */}
          {/* ════════════════════════════════════════════ */}
          {view === 'signup-register' && (
            <>
              <div className="auth-header">
                <button className="auth-back-btn" onClick={() => { setView('login'); setError(''); setSuccess(''); }}>
                  <ArrowLeft size={20} />
                </button>
                <div className="auth-icon-circle"><User size={24} /></div>
                <h2 className="auth-title">Create Account</h2>
                <p className="auth-subtitle">Fill in your information to join us</p>
              </div>

              <div className="auth-input-group">
                <div className="auth-input-row">
                  <div className="auth-field">
                    <label className="auth-label">FIRST NAME <span className="auth-page-required">*</span></label>
                    <div className="auth-input-wrapper">
                      <User size={18} className="auth-input-icon" />
                      <input
                        type="text"
                        placeholder="First Name"
                        value={signupFirstName}
                        onChange={(e) => { setSignupFirstName(e.target.value.replace(/[^a-zA-Z\s]/g, '')); setError(''); }}
                        className="auth-input auth-input-with-icon"
                      />
                    </div>
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">LAST NAME <span className="auth-page-required">*</span></label>
                    <div className="auth-input-wrapper">
                      <User size={18} className="auth-input-icon" />
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={signupLastName}
                        onChange={(e) => { setSignupLastName(e.target.value.replace(/[^a-zA-Z\s]/g, '')); setError(''); }}
                        className="auth-input auth-input-with-icon"
                      />
                    </div>
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-label">MOBILE NUMBER <span className="auth-page-required">*</span></label>
                  <div className="auth-input-wrapper">
                    <Phone size={18} className="auth-input-icon" />
                    <input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={signupPhone}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '');
                        if (v.length <= 10) { setSignupPhone(v); setError(''); }
                      }}
                      maxLength={10}
                      className="auth-input auth-input-with-icon"
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-label">EMAIL <span className="auth-page-required">*</span></label>
                  <div className="auth-input-wrapper">
                    <Mail size={18} className="auth-input-icon" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={signupEmail}
                      onChange={(e) => { setSignupEmail(e.target.value); setError(''); }}
                      className="auth-input auth-input-with-icon"
                    />
                  </div>
                </div>
              </div>

              <TermsCheckbox />

              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleSignupRegister}
                disabled={loading || !signupFirstName.trim() || !signupLastName.trim() || !signupPhone || signupPhone.length < 10 || !acceptTerms}
                className="auth-btn auth-btn-primary"
              >
                {loading ? <><Loader size={18} className="auth-spinner" /> Creating Account...</> : 'Create Account'}
              </button>

              <div className="auth-switch-mode">
                <span>Already have an account?</span>
                <button onClick={() => { setView('login'); setError(''); setSuccess(''); }} className="auth-switch-btn">
                  Login
                </button>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════ */}
          {/* FORGOT PASSWORD: Step 1 — Enter Email */}
          {/* ════════════════════════════════════════════ */}
          {view === 'forgot' && (
            <>
              <div className="auth-header">
                <button className="auth-back-btn" onClick={() => { setView('login'); setError(''); setSuccess(''); }}>
                  <ArrowLeft size={20} />
                </button>
                <div className="auth-icon-circle"><Lock size={24} /></div>
                <h2 className="auth-title">Forgot Password</h2>
                <p className="auth-subtitle">Enter your email to receive a reset code</p>
              </div>

              <div className="auth-input-group">
                <div className="auth-input-wrapper">
                  <Mail size={18} className="auth-input-icon" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={forgotEmail}
                    onChange={(e) => { setForgotEmail(e.target.value); setError(''); }}
                    className="auth-input auth-input-with-icon"
                  />
                </div>
              </div>

              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleForgotSendCode}
                disabled={loading || !forgotEmail.trim()}
                className="auth-btn auth-btn-primary"
              >
                {loading ? <><Loader size={18} className="auth-spinner" /> Sending...</> : 'Send Reset Code'}
              </button>

              <div className="auth-switch-mode">
                <span>Remember your password?</span>
                <button onClick={() => { setView('login'); setError(''); }} className="auth-switch-btn">
                  Log In
                </button>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════ */}
          {/* FORGOT PASSWORD: Step 2 — Enter Code */}
          {/* ════════════════════════════════════════════ */}
          {view === 'forgot-code' && (
            <>
              <div className="auth-header">
                <button className="auth-back-btn" onClick={() => {
                  setView('forgot');
                  setError('');
                  setSuccess('');
                  localStorage.removeItem('fpCodeExpiry');
                  setForgotTimer(0);
                }}>
                  <ArrowLeft size={20} />
                </button>
                <div className="auth-icon-circle"><Mail size={24} /></div>
                <h2 className="auth-title">Enter Reset Code</h2>
                <p className="auth-subtitle">We sent a code to <strong>{forgotEmail}</strong></p>

              </div>

              {forgotTimer > 0 && (
                <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: forgotTimer <= 60 ? '#e53e3e' : '#666' }}>
                  Code expires in <strong>{formatForgotTimer(forgotTimer)}</strong>
                </div>
              )}
              {forgotTimer <= 0 && !loading && (
                <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: '#e53e3e', fontWeight: '600' }}>
                  Code has expired. Please resend.
                </div>
              )}

              <div className="auth-input-group">
                <OtpBoxInput length={4} value={forgotCode} onChange={(val) => { setForgotCode(val); setError(''); setSuccess(''); }} />
              </div>

              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleForgotVerifyCode}
                disabled={loading || !forgotCode.trim() || forgotTimer <= 0}
                className="auth-btn auth-btn-primary"
              >
                {loading && forgotTimer > 0 ? <><Loader size={18} className="auth-spinner" /> Verifying...</> : 'Verify Code'}
              </button>

              <div className="auth-footer-links">
                {forgotTimer <= 0 && (
                  <button onClick={() => { setForgotCode(''); setError(''); handleForgotSendCode(); }} disabled={loading}
                    style={{ marginTop: '8px', padding: '10px 24px', background: '#fff', color: '#e65100', border: '2px solid #e65100', borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', width: '100%' }}>
                    {loading ? <><Loader size={18} className="auth-spinner" /> Sending...</> : 'Resend Code'}
                  </button>
                )}
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════ */}
          {/* FORGOT PASSWORD: Step 3 — Set New Password */}
          {/* ════════════════════════════════════════════ */}
          {view === 'forgot-reset' && (
            <>
              <div className="auth-header">
                <div className="auth-icon-circle auth-icon-success"><Lock size={24} /></div>
                <h2 className="auth-title">Set New Password</h2>
                <p className="auth-subtitle">Create a new password for your account</p>
              </div>

              <div className="auth-input-group">
                <div className="auth-input-wrapper">
                  <Lock size={18} className="auth-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New Password (min 6 characters)"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    className="auth-input auth-input-with-icon"
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="auth-input-wrapper">
                  <Lock size={18} className="auth-input-icon" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    className="auth-input auth-input-with-icon"
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && <div className="auth-error"><AlertCircle size={14} /><span>{error}</span></div>}

              <button
                onClick={handleForgotResetPassword}
                disabled={loading || !newPassword || !confirmPassword}
                className="auth-btn auth-btn-primary"
              >
                {loading ? <><Loader size={18} className="auth-spinner" /> Resetting...</> : 'Reset Password'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
